import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiPercent, FiLock, FiUnlock, FiCopy, FiCheck, FiLayers, FiTrendingUp } = FiIcons;

// Utility for formatting numbers with commas
const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "0.00";
  return num.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const EditableResultCard = ({
  icon,
  label,
  value,
  onChange,
  field,
  prefix = '',
  suffix = '',
  iconBg,
  iconColor,
  delay,
  allowArrowKeys = false,
  stickyMargin,
  toggleStickyMargin,
  isMarginField = false,
  darkMode,
  className = "",
  subValue = null,
  subLabel = ""
}) => {
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const rawVal = e.target.value;
    const sanitizedVal = rawVal.replace(/[^\d.]/g, '');
    const parts = sanitizedVal.split('.');
    const finalVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : sanitizedVal;

    if (finalVal === '' || /^\d*\.?\d*$/.test(finalVal)) {
      onChange(field, finalVal);
    }
  };

  const copyToClipboard = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (!allowArrowKeys) return;
    const currentValue = parseFloat(value) || 0;
    let newValue = currentValue;
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newValue = Math.min(currentValue + 1, 99.99);
        onChange(field, newValue.toFixed(2));
        break;
      case 'ArrowDown':
        e.preventDefault();
        newValue = Math.max(currentValue - 1, 0);
        onChange(field, newValue.toFixed(2));
        break;
      default:
        break;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay }}
      className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 border border-gray-200 dark:border-gray-600 transition-colors duration-300 ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={`p-1.5 rounded ${iconBg} dark:opacity-90`}>
            <SafeIcon icon={icon} className={`text-base ${iconColor}`} />
          </div>
          <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">{label}</p>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={copyToClipboard}
            className={`p-1 rounded-md transition-all ${copied ? 'text-green-500' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-[10px]" />
          </button>
          {isMarginField && (
            <button
              onClick={toggleStickyMargin}
              className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${stickyMargin ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${stickyMargin ? 'translate-x-3' : 'translate-x-0.5'}`} />
            </button>
          )}
        </div>
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-400 dark:text-gray-500 text-sm">{prefix}</span>
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="0.00"
          className="w-full pl-6 pr-8 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-sm focus:ring-1 focus:ring-blue-500 transition-colors font-bold text-lg text-gray-800 dark:text-white"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-gray-400 dark:text-gray-500 text-base font-bold">{suffix}</span>
        </div>
      </div>
      {subValue !== null && (
        <div className="mt-1.5 flex justify-between items-center px-1 border-t border-gray-100 dark:border-gray-700 pt-1">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-tighter">{subLabel}</span>
          <span className="text-xs text-gray-600 dark:text-gray-300 font-mono font-bold">${formatCurrency(subValue)}</span>
        </div>
      )}
    </motion.div>
  );
};

const SummaryRow = ({ label, value, colorClass, isPrimary = false, isTotal = false }) => (
  <div className={`flex justify-between items-center py-2 ${isPrimary ? 'bg-blue-100/30 dark:bg-blue-900/10 -mx-6 px-6 py-3 my-1 border-y border-blue-100 dark:border-blue-900/30' : ''}`}>
    <span className={`text-[10px] font-bold uppercase tracking-wider ${isPrimary ? 'text-blue-700 dark:text-blue-300 scale-110 origin-left' : 'text-gray-500 dark:text-gray-400'}`}>{label}</span>
    <span className={`${isPrimary ? 'text-2xl font-black' : isTotal ? 'text-lg font-bold' : 'text-lg font-medium'} ${colorClass}`}>${formatCurrency(value)}</span>
  </div>
);

const EditableResultsDisplay = ({ values, onChange, stickyMargin, toggleStickyMargin, darkMode }) => {
  const GST_RATE = 1.10; // 10% GST
  
  const qty = parseFloat(values.qty) || 0;
  const unitRevenue = parseFloat(values.revenue) || 0;
  const unitProfit = parseFloat(values.profit) || 0;
  const unitCost = parseFloat(values.costPrice) || 0;
  
  const unitCostIncGst = (unitCost * GST_RATE).toFixed(2);
  const unitRevenueIncGst = (unitRevenue * GST_RATE).toFixed(2);

  const totalRevenue = (unitRevenue * qty).toFixed(2);
  const totalRevenueIncGst = (unitRevenue * qty * GST_RATE).toFixed(2);
  const totalProfit = (unitProfit * qty).toFixed(2);
  const totalCost = (unitCost * qty).toFixed(2);

  const showTotals = qty > 1 && !isNaN(unitRevenue) && unitRevenue > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <EditableResultCard
          icon={FiDollarSign}
          label="Unit Cost"
          prefix="$"
          value={values.costPrice}
          onChange={onChange}
          field="costPrice"
          subValue={unitCostIncGst}
          subLabel="Inc. GST"
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          iconColor="text-blue-600 dark:text-blue-400"
          delay={0.1}
          darkMode={darkMode}
        />
        <EditableResultCard
          icon={FiLayers}
          label="Quantity"
          value={values.qty}
          onChange={onChange}
          field="qty"
          iconBg="bg-orange-100 dark:bg-orange-900/30"
          iconColor="text-orange-600 dark:text-orange-400"
          delay={0.15}
          darkMode={darkMode}
        />
      </div>

      <EditableResultCard
        icon={FiPercent}
        label="Margin Percentage"
        value={values.marginPercentage}
        onChange={onChange}
        field="marginPercentage"
        suffix="%"
        iconBg="bg-indigo-100 dark:bg-indigo-900/30"
        iconColor="text-indigo-600 dark:text-indigo-400"
        delay={0.2}
        allowArrowKeys={true}
        stickyMargin={stickyMargin}
        toggleStickyMargin={toggleStickyMargin}
        isMarginField={true}
        darkMode={darkMode}
      />

      <div className="grid grid-cols-2 gap-3">
        <EditableResultCard
          icon={FiTrendingUp}
          label="Unit Profit"
          prefix="$"
          value={values.profit}
          onChange={onChange}
          field="profit"
          iconBg="bg-green-100 dark:bg-green-900/30"
          iconColor="text-green-600 dark:text-green-400"
          delay={0.25}
          darkMode={darkMode}
        />
        <EditableResultCard
          icon={FiDollarSign}
          label="Unit Revenue"
          prefix="$"
          value={values.revenue}
          onChange={onChange}
          field="revenue"
          subValue={unitRevenueIncGst}
          subLabel="Inc. GST"
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          iconColor="text-purple-600 dark:text-purple-400"
          delay={0.3}
          darkMode={darkMode}
        />
      </div>

      <AnimatePresence>
        {showTotals && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-blue-50 dark:bg-gray-800/80 rounded-2xl p-6 mt-3 border border-blue-200 dark:border-blue-500/20 shadow-lg shadow-blue-500/5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-[11px] font-black text-blue-700 dark:text-blue-400 uppercase tracking-[0.2em]">Batch Summary ({qty} units)</span>
              </div>
              <div className="space-y-0.5 divide-y divide-blue-100 dark:divide-gray-700">
                <SummaryRow label="Total Cost (Ex. GST)" value={totalCost} colorClass="text-gray-500 dark:text-gray-400" />
                <SummaryRow label="Total Profit" value={totalProfit} colorClass="text-green-600 dark:text-green-400" />
                <SummaryRow 
                  label="Total Revenue" 
                  value={totalRevenue} 
                  colorClass="text-blue-700 dark:text-blue-400" 
                  isPrimary={true} 
                />
                <SummaryRow label="Total Revenue (Inc. GST)" value={totalRevenueIncGst} colorClass="text-gray-600 dark:text-gray-400" isTotal={true} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditableResultsDisplay;